import * as React from 'react';
import PropTypes from 'prop-types';

import FormQuestion from './FormQuestion';
import { customContentElementTypes, questionContentElementTypes } from '../../constants';
import QuestionnaireTable from './formResponseTypes/customTypes/QuestionnaireTable';
import BullsEyeQuestions from './formResponseTypes/customTypes/BullsEyeQuestions';
import BubbleChoiceQuestions from './formResponseTypes/customTypes/BubbleChoiceQuestions';
import QuizResponse from './formResponseTypes/customTypes/QuizResponse';
import Quiz from './formResponseTypes/customTypes/Quiz';
import RouteSelection from './formResponseTypes/customTypes/RouteSelection';
import CollapsedTable from './formResponseTypes/customTypes/CollapsedTable';

class TaskForm extends React.Component {
    handleResponseUpdate = question => (responseName, value) => {
        if (this.props.readOnly) {
            return;
        }

        this.props.onResponse([{ questionId: question.id, questionKey: question.key, [responseName]: value }]);
    }

    render() {
        const {
            element, disabled, readOnly,
            FormQuestionProps, QuestionnaireTableProps, contentState, showValidationErrors,
            taskResponses, getProgramItem, getSpecificTaskResponses, taskId, small,
        } = this.props;

        const questions = element.questions || [];
        const responses = taskResponses || {};
        const elementSettings = element.settings || {};

        switch (element.elementType) {
        case questionContentElementTypes.bullseye:
            return (
                <BullsEyeQuestions
                    questions={questions}
                    handleResponseUpdate={this.handleResponseUpdate}
                    responses={responses}
                    readOnly={readOnly}
                    small={small}
                />
            );
        case questionContentElementTypes.questionnaire:
            return (
                <QuestionnaireTable
                    questions={questions}
                    responses={responses}
                    onChange={this.handleResponseUpdate}
                    validationError={showValidationErrors}
                    disabled={disabled}
                    elementSettings={elementSettings}
                    readOnly={readOnly}
                    {...QuestionnaireTableProps}
                />
            );
        case questionContentElementTypes.bubbleChoice:
            return questions.map(q => (
                <BubbleChoiceQuestions
                    key={q.id}
                    question={q}
                    response={responses[q.id]?.response}
                    handleResponseUpdate={this.handleResponseUpdate(q)}
                    readOnly={readOnly}
                />
            ));
        case customContentElementTypes.quizResponse:
            return (
                <QuizResponse
                    element={element}
                    contentState={contentState}
                    getProgramItem={getProgramItem}
                    taskId={taskId}
                    getSpecificTaskResponses={getSpecificTaskResponses}
                    handleResponseUpdate={this.handleResponseUpdate}
                />
            );
        case questionContentElementTypes.quiz:
            return (
                <Quiz
                    element={element}
                    contentState={contentState}
                    getProgramItem={getProgramItem}
                    taskId={taskId}
                    getSpecificTaskResponses={getSpecificTaskResponses}
                    handleResponseUpdate={this.handleResponseUpdate}
                    responses={responses}
                    disabled={disabled}
                    readOnly={readOnly}
                    FormQuestionProps={FormQuestionProps}
                />
            );
        case customContentElementTypes.routeSelection:
            return (
                <RouteSelection
                    questions={questions}
                    handleResponseUpdate={this.handleResponseUpdate}
                    responses={responses}
                    readOnly={readOnly}
                />
            );
        case (questionContentElementTypes.collapsedTable):
            return questions.map(q => (
                <CollapsedTable
                    key={q.id}
                    question={q}
                    responses={responses[q.id]?.response}
                    handleResponseUpdate={this.handleResponseUpdate(q)}
                    readOnly={readOnly}
                />
            ));
        default:
            return questions.map(q => (
                <FormQuestion
                    key={q.id}
                    question={q}
                    response={responses[q.id]?.response}
                    handleResponseUpdate={this.handleResponseUpdate(q)}
                    showRequired={showValidationErrors}
                    disabled={disabled}
                    contentState={contentState}
                    readOnly={readOnly}
                    elementSettings={elementSettings}
                    {...FormQuestionProps}
                />
            ));
        }
    }
}

TaskForm.propTypes = {
    element: PropTypes.object.isRequired,
    onResponse: PropTypes.func.isRequired,
    getProgramItem: PropTypes.func.isRequired,
    getSpecificTaskResponses: PropTypes.func.isRequired,
    FormQuestionProps: PropTypes.object,
    QuestionnaireTableProps: PropTypes.object,
    taskResponses: PropTypes.object,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    contentState: PropTypes.string,
    taskId: PropTypes.string.isRequired,
    showValidationErrors: PropTypes.bool,
    small: PropTypes.bool,
};

TaskForm.defaultProps = {
    FormQuestionProps: {},
    QuestionnaireTableProps: {},
    taskResponses: null,
    disabled: false,
    contentState: null,
    readOnly: false,
    showValidationErrors: false,
    small: false,
};

export default TaskForm;
